import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Select, DatePicker, Row, Col, Spin } from "antd";
import { INVOICE_DATA_SOURCE_STATUS_LABELS, SOURCE_TYPE_LABELS, STANDARD_COST_TYPE_OPTIONS } from "../../constants";
import { contractManagementService } from "../../../../api/enhancedApi";
import { DEFAULT_FILTER_VALUES, transformFormToApiParams } from "../hooks/useInvoiceDataSourceFilters";
import AirportSelector from "../../../../components/common/AirportSelector";
import { fetchUsers } from "../../../../api/userManagement";

const { RangePicker } = DatePicker;

/**
 * 账单数据源筛选组件
 * 参考 UnifiedFlightFilter 的实现，采用实时自动搜索模式：
 * - 筛选条件变更时自动触发搜索（带防抖）
 * - 移除显式的搜索按钮，只保留重置按钮
 * - 所有筛选条件平铺在多行中
 */
const InvoiceDataSourceFilter = ({ onSearch, onReset, loading, defaultCreatedByUserId }) => {
    const [form] = Form.useForm();
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // 防抖引用 - 用于文本输入字段
    const searchDebounceRef = useRef(null);

    // 将状态标签转换为选项格式
    const statusOptions = Object.entries(INVOICE_DATA_SOURCE_STATUS_LABELS).map(([key, label]) => ({
        value: key,
        label: label,
    }));

    // 将来源类型标签转换为选项格式
    const sourceTypeOptions = Object.entries(SOURCE_TYPE_LABELS).map(([key, label]) => ({
        value: key,
        label: label,
    }));

    // 加载供应商列表
    useEffect(() => {
        const loadSuppliers = async () => {
            setLoadingSuppliers(true);
            try {
                const response = await contractManagementService.businessPartner.getSuppliers({
                    is_active: true,
                    page: 1,
                    page_size: 500,
                });

                if (response.data && Array.isArray(response.data.items)) {
                    setSuppliers(
                        response.data.items.map((supplier) => ({
                            value: supplier.id,
                            label: supplier.code ? `${supplier.name} (${supplier.code})` : supplier.name,
                            code: supplier.code,
                        })),
                    );
                }
            } catch (error) {
                console.error("获取供应商列表失败:", error);
            } finally {
                setLoadingSuppliers(false);
            }
        };

        loadSuppliers();
    }, []);

    // 加载用户列表
    useEffect(() => {
        const loadUsers = async () => {
            setLoadingUsers(true);
            try {
                const data = await fetchUsers({ page: 1, page_size: 200 });
                if (data && Array.isArray(data.items)) {
                    setUsers(
                        data.items.map((u) => ({
                            value: u.id,
                            label: u.full_name || u.username,
                        })),
                    );
                }
            } catch (error) {
                console.error("获取用户列表失败:", error);
            } finally {
                setLoadingUsers(false);
            }
        };

        loadUsers();
    }, []);

    // 设置默认创建人（仅在组件初始化时执行一次）
    const initializedRef = useRef(false);
    useEffect(() => {
        if (!initializedRef.current && defaultCreatedByUserId) {
            form.setFieldsValue({ created_by_user_id: defaultCreatedByUserId });
            initializedRef.current = true;
        }
    }, [defaultCreatedByUserId, form]);

    // 提交筛选条件 - 使用共享的转换函数
    const submitFilters = (values) => {
        const params = transformFormToApiParams(values);
        onSearch(params);
    };

    // 表单值变化处理 - 所有字段均带防抖，避免频繁请求
    const handleFormChange = (changedValues, allValues) => {
        // 文本输入字段：500ms 防抖（用户连续打字）
        // 下拉框/日期等字段：2000ms 防抖（用户连续多选）
        const textInputFields = ["batch_number", "file_path"];
        const isTextInput = Object.keys(changedValues).some((key) => textInputFields.includes(key));
        // 创建人切换是单选，无需等待连续操作，使用较短防抖
        const isQuickSelect = Object.keys(changedValues).some((key) => key === "created_by_user_id");
        const delay = isQuickSelect ? 300 : isTextInput ? 500 : 2000;

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            submitFilters(allValues);
        }, delay);
    };

    // 重置表单
    const handleReset = () => {
        // 清除防抖定时器
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        form.resetFields();
        onReset();
    };

    // 组件卸载时清除定时器
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    return (
        <Form form={form} layout="vertical" initialValues={DEFAULT_FILTER_VALUES.form} onValuesChange={handleFormChange}>
            {/* 第一行：状态、批次号、供应商、费用类型 */}
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="状态" name="status">
                        <Select mode="multiple" placeholder="选择状态" maxTagCount="responsive" allowClear showSearch options={statusOptions} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="批次号" name="batch_number">
                        <Input placeholder="输入批次号关键词" allowClear />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="供应商" name="supplier_id">
                        <Select mode="multiple" placeholder="请搜索或选择供应商" maxTagCount="responsive" allowClear showSearch options={suppliers} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} loading={loadingSuppliers} notFoundContent={loadingSuppliers ? <Spin size="small" /> : "暂无供应商"} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="费用类型（预分类）" name="cost_type">
                        <Select mode="multiple" placeholder="选择费用类型" maxTagCount="responsive" allowClear showSearch options={STANDARD_COST_TYPE_OPTIONS} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                    </Form.Item>
                </Col>
            </Row>

            {/* 第二行：来源类型、关联机场、创建人、创建时间 */}
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="来源类型" name="source_type">
                        <Select mode="multiple" placeholder="选择来源类型" allowClear maxTagCount="responsive" showSearch options={sourceTypeOptions} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="关联机场" name="airport_code">
                        <AirportSelector mode="multiple" valueType="iata_code" placeholder="选择机场" maxTagCount="responsive" allowClear style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="创建人" name="created_by_user_id">
                        <Select
                            placeholder="全部用户"
                            allowClear
                            showSearch
                            options={users}
                            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                            loading={loadingUsers}
                            notFoundContent={loadingUsers ? <Spin size="small" /> : "暂无用户"}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="创建时间" name="date_range">
                        <RangePicker style={{ width: "100%" }} placeholder={["开始日期", "结束日期"]} format="YYYY-MM-DD" />
                    </Form.Item>
                </Col>
            </Row>

            {/* 第三行：综合搜索 */}
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                    <Form.Item label="综合搜索" name="file_path">
                        <Input placeholder="文件名/供应商/批次号/机场" allowClear />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default InvoiceDataSourceFilter;
